import { useEffect } from 'react';

/**
 * Modal — centered overlay dialog.
 *
 * Props:
 *   isOpen, onClose: visibility control (pair with useModal())
 *   title, children, footer: content slots
 *   size: 'sm' | 'md' (default) | 'lg'
 */
export default function Modal({ isOpen, onClose, title, children, footer, size = 'md' }) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const maxWidth = { sm: 380, md: 520, lg: 760 }[size] || 520;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(11,31,56,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full"
        style={{ maxWidth, borderRadius: 18, boxShadow: 'var(--shadow-lg)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        {(title || onClose) && (
          <div className="flex items-center justify-between" style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)' }}>
            <h3 style={{ fontSize: 20 }}>{title}</h3>
            {onClose && (
              <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-lg"
                style={{ width: 32, height: 32, background: 'var(--bg-soft)' }} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer && (
          <div className="flex justify-end gap-2" style={{ padding: '16px 24px', borderTop: '1px solid var(--line)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
